import { useRoute, useRouter } from 'vue-router'
import { computed } from 'vue'
import { trackEvent } from '@/services/analytics-service'
import { useCoBrandedCardsAndSubscribedBonuses } from './coBrandPartnersAndSubscribedBonuses'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import type { MenuEntry, MenuSection, NavigationMenu } from '@/types/navigation'
import { buildAabMenu } from '@/helpers/navigation/aab'
import { buildB2cMenu } from '@/helpers/navigation/b2c'
import { useCompanyStore } from '@/stores/company'
import { useEmployeeStore } from '@/stores/employee'

export { type MenuEntry }

const filterDisplayableEntries = (entries: NavigationMenu): NavigationMenu => {
  if (entries.length === 0) return entries
  const isMenuSectionArray = (entry: NavigationMenu): entry is MenuSection[] =>
    entry.length > 0 && 'children' in entry[0]

  if (isMenuSectionArray(entries)) {
    return entries.flatMap((section) => {
      const children = section.children.filter(
        (child) => child.display === undefined || child.display()
      )
      return children.length === 0 ? [] : [{ ...section, children }]
    })
  }

  return entries.filter((entry) => entry.display === undefined || entry.display())
}

export const useNavigation = () => {
  const route = useRoute()
  const router = useRouter()
  const userStore = useUserStore()
  const { getCoBrandedPartners } = useCoBrandedCardsAndSubscribedBonuses()
  const companyStore = useCompanyStore()
  const employeeStore = useEmployeeStore()

  const { coBrandedCards, hasAutoConversion, userHasAtLeastOneSubscriptionCard } =
    storeToRefs(userStore)
  const hasAirlineOrAmexPartner = computed(
    () => getCoBrandedPartners(coBrandedCards.value).length > 0
  )

  const isAab = computed(() => route.matched.some((r) => r.name === 'aab'))

  const isAuthorizedForAab = computed(
    () => !!companyStore.company && employeeStore.isAuthorizedForAab
  )

  const items = computed(() => {
    const menu = isAab.value
      ? buildAabMenu()
      : buildB2cMenu({
          hasSubscriptionCard: userHasAtLeastOneSubscriptionCard.value,
          hasCoBrandAirline: hasAirlineOrAmexPartner.value,
          hasAutoConversion: hasAutoConversion.value
        })
    return filterDisplayableEntries(menu)
  })

  const activeRoute = computed(() => {
    const menuRouteNames = new Set(
      items.value.flatMap((g) =>
        'children' in g ? g.children.map((c) => c.routeName) : [g.routeName]
      )
    )
    const matched = route.matched.map((r) => r.name as string).find((n) => menuRouteNames.has(n))
    return matched ?? (route.name as string)
  })

  const updateSelectedItem = (entry: MenuEntry) => {
    if (entry.tracking) {
      trackEvent('GTM_MENU_ITEM', entry.tracking)
    }

    if (entry.url) {
      window.open(entry.url, '_blank')
    } else if (entry.routeName) {
      router.push({ name: entry.routeName })
    }
  }

  const isActive = (entry: MenuEntry) => entry.routeName === activeRoute.value

  return {
    items,
    isAab,
    activeRoute,
    isActive,
    isAuthorizedForAab,
    updateSelectedItem
  }
}
